import React from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

/**
 * Error boundary specifically designed for page-level components.
 * Provides a more contextual error message with the page name.
 */
export default class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'production') {
      fetch('/api/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.toString(),
          errorInfo,
          url: window.location.href,
          userAgent: navigator.userAgent,
          page: this.props.pageName,
          timestamp: new Date().toISOString(),
        }),
      });
    } else {
      console.error(`[PageErrorBoundary] (${this.props.pageName})`, error, errorInfo);
    }
    if (this.props.onError) this.props.onError(error, errorInfo);
  }

  handleRetry = () => {
    if (this.props.onRetry) {
      this.props.onRetry();
    }
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { pageName } = this.props;
    if (this.state.hasError) {
      const { error } = this.state;
      const fallback = (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            p: 4,
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%',
              textAlign: 'center',
              borderTop: '4px solid #f44336',
            }}
          >
            <ErrorOutlineIcon color="error" sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h5" color="error" gutterBottom>
              {pageName ? `Error in ${pageName}` : 'Page Error'}
            </Typography>
            {error && error.message ? (
              <>
                {/network/i.test(error.message) ? (
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Network error loading {pageName}.
                  </Typography>
                ) : /permission|unauthorized|forbidden/i.test(error.message) ? (
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Permission error: You do not have access to this page.
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ mb: 3 }}>
                    Unexpected error loading {pageName}: {error.message}
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body1" sx={{ mb: 3 }}>
                We encountered a problem loading this page. Our team has been notified.
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" color="primary" onClick={this.handleRetry}>
                Try Again
              </Button>
              <Button variant="contained" color="primary" onClick={this.handleReload}>
                Reload Page
              </Button>
            </Box>
          </Paper>
        </Box>
      );
      return fallback;
    }

    return this.props.children;
  }
}
